import React from 'react';
import { Camera, Film, Shield, ShieldCheck, Menu, X, Instagram, Youtube, Globe, Lock } from 'lucide-react';
import { motion } from 'motion/react';
import { useLanguage } from '../context/LanguageContext';

interface HeaderProps {
  onClientAreaClick: () => void;
  studioName: string;
  logoUrl?: string;
  onAdminToggle: () => void;
  isAdminOpen: boolean;
  isAdminAuthenticated?: boolean;
}

export function Header({ studioName, logoUrl, onAdminToggle, isAdminOpen, isAdminAuthenticated = false, onClientAreaClick }: HeaderProps) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);
  const { lang, setLang, t } = useLanguage();

  const scrollToSection = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-theme-bg/60 backdrop-blur-md border-b border-theme-border">
      <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
        {/* Brand Logo */}
        <button
          onClick={() => scrollToSection('hero')}
          className="flex items-center gap-2 sm:gap-3 text-left group shrink-0"
          id="logo-btn"
        >
          {logoUrl ? (
            <img 
              src={logoUrl} 
              alt={studioName} 
              className="h-8 sm:h-10 w-auto object-contain shrink-0 max-w-[140px]" 
              onError={(e) => {
                // If image fails to load, hide image and show default
                (e.target as HTMLElement).style.display = 'none';
              }}
            />
          ) : (
            <div className="flex gap-1 items-center shrink-0">
              <Camera className="w-4 h-4 sm:w-5 sm:h-5 text-theme-accent group-hover:rotate-12 transition-transform duration-300" />
              <span className="text-[10px] sm:text-xs font-mono text-theme-secondary">x</span>
              <Film className="w-4 h-4 sm:w-5 sm:h-5 text-theme-text group-hover:-rotate-12 transition-transform duration-300" />
            </div>
          )}
          <div className="min-w-0">
            <h1 className="text-sm sm:text-base md:text-lg font-serif tracking-wider text-theme-text font-semibold group-hover:text-theme-accent transition-colors whitespace-nowrap truncate">
              {studioName}
            </h1>
            <p className="hidden sm:block text-[8px] sm:text-[9px] uppercase tracking-[0.2em] sm:tracking-[0.25em] text-theme-secondary truncate">
              {t('nav_sub_brand')}
            </p>
          </div>
        </button>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-8">
          <button
            onClick={() => scrollToSection('galleries')}
            className="text-sm font-medium text-theme-secondary hover:text-theme-text transition-colors uppercase tracking-wider text-xs"
            id="nav-galleries"
          >
            {t('nav_portfolio')}
          </button>
          <button
            onClick={() => scrollToSection('duo')}
            className="text-sm font-medium text-theme-secondary hover:text-theme-text transition-colors uppercase tracking-wider text-xs"
            id="nav-duo"
          >
            {t('nav_duo')}
          </button>
          <button
            onClick={() => scrollToSection('contact')}
            className="text-sm font-medium text-theme-secondary hover:text-theme-text transition-colors uppercase tracking-wider text-xs"
            id="nav-contact"
          >
            {t('nav_contact')}
          </button>
        </nav>

        {/* Action Buttons */}
        <div className="hidden md:flex items-center gap-4">
          {/* Elegant Language Switcher */}
          <div className="flex items-center gap-1 p-1 bg-black/20 border border-white/10 rounded-full font-mono text-[10px]">
            <button
              onClick={() => setLang('fr')}
              className={`px-2.5 py-1 rounded-full transition-all cursor-pointer font-bold ${
                lang === 'fr'
                  ? 'bg-theme-accent text-theme-bg'
                  : 'text-theme-secondary hover:text-theme-text'
              }`}
              title="Version Française"
            >
              FR
            </button>
            <button
              onClick={() => setLang('en')}
              className={`px-2.5 py-1 rounded-full transition-all cursor-pointer font-bold ${
                lang === 'en'
                  ? 'bg-theme-accent text-theme-bg'
                  : 'text-theme-secondary hover:text-theme-text'
              }`}
              title="English Version"
            >
              EN
            </button>
          </div>



          <button
            onClick={onAdminToggle}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs tracking-wider transition-all duration-300 ${
              isAdminAuthenticated
                ? 'bg-amber-400/20 border-amber-400 text-amber-300 font-bold'
                : isAdminOpen
                ? 'bg-theme-accent/20 border-theme-accent text-theme-accent'
                : 'bg-theme-card border-theme-border text-theme-secondary hover:bg-theme-card-hover hover:border-theme-border-hover'
            }`}
            id="admin-toggle-btn"
          >
            {isAdminAuthenticated ? <ShieldCheck className="w-3.5 h-3.5 text-amber-400" /> : <Shield className="w-3.5 h-3.5" />}
            <span>
              {isAdminAuthenticated
                ? (isAdminOpen ? 'Fermer Panneau' : 'Administration (Connecté)')
                : (isAdminOpen ? t('nav_admin_exit') : t('nav_admin_enter'))}
            </span>
          </button>

          <button
            onClick={onClientAreaClick}
            className="text-theme-secondary hover:text-theme-accent transition-colors text-xs font-mono uppercase tracking-wider flex items-center gap-1.5"
            title="Accès Client"
          >
            <Lock className="w-3.5 h-3.5" />
            <span>Espace Client</span>
          </button>
          <button
            onClick={() => scrollToSection('contact')}
            className="bg-theme-accent text-theme-bg hover:opacity-90 font-semibold text-xs uppercase tracking-wider px-5 py-2.5 rounded-none transition-all duration-300 shadow-md animate-none"
            id="cta-contact-btn"
          >
            {t('nav_cta')}
          </button>
        </div>

        {/* Mobile Actions with responsive Language Toggle */}
        <div className="flex items-center gap-2 md:hidden">
          {/* Compact language toggle for mobile screens */}
          <button
            onClick={() => setLang(lang === 'fr' ? 'en' : 'fr')}
            className="px-2.5 py-1.5 bg-black/20 border border-white/10 rounded-full font-mono text-[10px] text-theme-accent font-bold uppercase cursor-pointer flex items-center gap-1"
            title="Changer de langue / Change language"
            aria-label="Changer la langue / Change language"
          >
            <Globe className="w-3 h-3 text-theme-secondary" />
            <span>{lang === 'fr' ? 'FR' : 'EN'}</span>
          </button>

          <button
            onClick={onAdminToggle}
            className="p-2 rounded-full border border-theme-border text-theme-secondary hover:text-theme-accent"
            id="mobile-admin-btn"
            aria-label="Espace d'administration du site"
            title="Espace d'administration"
          >
            <Shield className="w-4 h-4" />
            <span className="sr-only">Espace d'administration</span>
          </button>
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="p-2 text-theme-text hover:text-theme-accent"
            id="mobile-menu-btn"
            aria-label={isMobileMenuOpen ? "Fermer le menu de navigation mobile" : "Ouvrir le menu de navigation mobile"}
            title={isMobileMenuOpen ? "Fermer le menu" : "Ouvrir le menu"}
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            <span className="sr-only">{isMobileMenuOpen ? "Fermer le menu" : "Ouvrir le menu"}</span>
          </button>
        </div>
      </div>

      {/* Mobile Drawer */}
      {isMobileMenuOpen && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          className="md:hidden bg-theme-card border-b border-theme-border py-6 px-6 space-y-4 absolute w-full left-0 right-0 z-40 shadow-xl"
        >
          <button
            onClick={() => scrollToSection('galleries')}
            className="block w-full text-left py-2 text-theme-secondary hover:text-theme-text uppercase tracking-wider text-xs font-semibold"
          >
            {t('nav_portfolio')}
          </button>
          <button
            onClick={() => scrollToSection('duo')}
            className="block w-full text-left py-2 text-theme-secondary hover:text-theme-text uppercase tracking-wider text-xs font-semibold"
          >
            {t('nav_duo')}
          </button>
          <button
            onClick={() => scrollToSection('contact')}
            className="block w-full text-left py-2 text-theme-secondary hover:text-theme-text uppercase tracking-wider text-xs font-semibold"
          >
            {t('nav_contact')}
          </button>
          <button
            onClick={() => { setIsMobileMenuOpen(false); onClientAreaClick(); }}
            className="block w-full text-left py-2 text-theme-accent hover:text-theme-text uppercase tracking-wider text-xs font-semibold flex items-center gap-2"
          >
            <Lock className="w-4 h-4" />
            Espace Client
          </button>



          <div className="pt-4 border-t border-theme-border flex flex-col gap-3">
            <button
              onClick={() => scrollToSection('contact')}
              className="w-full text-center bg-theme-accent text-theme-bg py-3 text-xs uppercase tracking-widest font-semibold"
            >
              {t('nav_cta')}
            </button>
          </div>
        </motion.div>
      )}
    </header>
  );
}

